import axios from "axios";
import { DropDownBox, LoadPanel, Popup } from "devextreme-react";
import DataGrid, { Column, HeaderFilter, Selection } from "devextreme-react/data-grid";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DynamicTabb } from "../../components/DynamicTab/DynamicTab";
import { Toster } from "../../components/Toster/Toster";
import { handleGetLocal } from "../../services/auth-service";
import AdminCrumbs from "./components/AdminCrumbs";
import MergeFeeConfirmationPopup from "./MergeFeeConfirmationPopup";
import "./MergeFees.css";
import { useScreenPermission } from "../../hooks/useScreenPermission";

const MergeFees = () => {
  const [mergeFeeData, setMergeFeeData] = useState([]);
  const [mergeToFee, setMergeToFee] = useState(null);
  const [toastConfig, setToastConfig] = useState({
    isVisible: false,
    type: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const gridRef = useRef(null);
  const navigate = useNavigate();
  const pathname = handleGetLocal("pathURL");
  const [selectedRows, setSelectedRows] = useState([]);
  const dropDownRef = useRef(null);
  const [isDropDownOpen, setIsDropDownOpen] = useState(false);
  const [deactivateMergeFromFees, setDeactivateMergeFromFees] = useState(true);
  const [isGridBoxOpened, setIsGridBoxOpened] = useState(false);
  const [mergeFeeConfirmationPopup, setMergeFeeConfirmationPopup] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [confirmationMessage, setConfirmationMessage] = useState("");

  const SCREEN_NAME = "MF3";
  const { permission, permissionLoading } = useScreenPermission(SCREEN_NAME, navigate);

  useEffect(() => {
    if (permissionLoading || !permission.view) return;

    getFeeData();
  }, [permissionLoading, permission.view]);
  const onGridBoxOpened = useCallback((e) => {
    if (e.name === "opened") {
      setIsGridBoxOpened(e.value);
    }
  }, []);

  const getFeeData = async () => {
    try {
      const val = await axios.get(`${process.env.REACT_APP_API_URL}/api/obligations/dropdown`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          Domain: `${pathname}`,
        },
      });

      console.log("obligation dropdown", val?.data?.data?.obligationFeeMergeDto);

      setMergeFeeData(val?.data?.data?.obligationFeeMergeDto || []);
    } catch (error) {
      console.log(error);
    }
  };

  const mergeFees = () => {
    if (selectedRows.length === 0) {
      setToastConfig({
        isVisible: true,
        message: "Please select fees to merge.",
        type: "error",
      });
      setTimeout(() => setToastConfig({ isVisible: false, message: "", type: "" }), 2000);
      return;
    }

    if (!mergeToFee) {
      setToastConfig({
        isVisible: true,
        message: "Please select merge destination fee.",
        type: "error",
      });
      setTimeout(() => setToastConfig({ isVisible: false, message: "", type: "" }), 2000);
      return;
    }

    const mergeToFeeObj = mergeFeeData.find((x) => x.gFeeScheduleFeeID === mergeToFee);

    const mergeToType = mergeToFeeObj?.sFeeScheduleTypeName;

    const invalidType = selectedRows.some((row) => row.sFeeScheduleTypeName !== mergeToType);

    if (invalidType) {
      setToastConfig({
        isVisible: true,
        message: "Please merge same fee type.",
        type: "error",
      });

      setTimeout(() => setToastConfig({ isVisible: false, message: "", type: "" }), 2000);
      return;
    }

    const payload = {
      mergeFromFeeIds: selectedRows.map((x) => x.gFeeScheduleFeeID),
      mergeToFeeId: mergeToFee,
      deactivateMergeFromFees: deactivateMergeFromFees,
    };
    setLoading(true);
    axios
      .post(`${process.env.REACT_APP_API_URL}/api/obligations/merge-fees`, payload, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          Domain: `${pathname}`,
        },
      })
      .then((res) => {
        setLoading(false);
        const data = res?.data;
        if (!data?.isSuccess) {
          setToastConfig({
            isVisible: true,
            message: data?.responseMessage || "Merge failed.",
            type: "error",
          });
          return;
        }
        setConfirmationMessage(data?.responseMessage || "Merge completed.");
        setMergeFeeConfirmationPopup(true);
        setToastConfig({
          isVisible: true,
          message: data?.responseMessage,
          type: "success",
        });

        setTimeout(() => setToastConfig({ isVisible: false, message: "", type: "" }), 2000);

        getFeeData();
        setSelectedRows([]);
        setMergeToFee(null);
      })
      .catch((error) => {
        setLoading(false);

        const message =
          error?.response?.data?.responseMessage || error?.response?.data?.message || "Server error occurred.";

        setToastConfig({
          isVisible: true,
          message: message,
          type: "error",
        });

        setTimeout(() => {
          setToastConfig({ isVisible: false, message: "", type: "" });
        }, 2000);
      });
  };

  const handleCancel = () => {
    setMergeToFee(null);
    setSelectedRows([]);
  };

  return (
  <>
    <Popup
      visible={mergeFeeConfirmationPopup}
      onHiding={() => setMergeFeeConfirmationPopup(false)}
      showTitle={false}
      width={650}
      height={200}
      showCloseButton={true}
      hideOnOutsideClick={false}
    >
      <MergeFeeConfirmationPopup
        mergeFeeConfirmationPopup={mergeFeeConfirmationPopup}
        setMergeFeeConfirmationPopup={setMergeFeeConfirmationPopup}
        totalCount={totalCount}
        message={confirmationMessage}
      />
    </Popup>

    <Toster message={toastConfig.message} type={toastConfig.type} visible={toastConfig.isVisible} />
    <LoadPanel shadingColor="true" visible={loading} delay={10} message="Processing, please be patient" />

    <AdminCrumbs title="Merge Fees" user="Data" />

    <div className="merge-fee-container">
      <DynamicTabb />

      {permissionLoading ? (
        <LoadPanel shadingColor="true" visible={true} delay={10} />
      ) : !permission.view ? (
        <div style={{ padding: "20px", fontWeight: 600 }}>You do not have View access for MF.3. Please contact your system administrator.</div>
      ) : (
        <>
          <p className="page-info-number">MF.3</p>

          <div className="merge-fee-content">
            <div style={{ width: "50%" }}>
              <h3 style={{ paddingBottom: "10px" }}>Merge From</h3>
              <DataGrid
                ref={gridRef}
                dataSource={mergeFeeData}
                keyExpr="gFeeScheduleFeeID"
                showBorders={true}
                height={700}
                columnAutoWidth={false}
                allowColumnResizing={true}
                columnResizingMode={"nextColumn"}
                onSelectionChanged={(e) => setSelectedRows(e.selectedRowsData)}
                selectedRowKeys={selectedRows.map((x) => x.gFeeScheduleFeeID)}
              >
                <Selection mode="multiple" showCheckBoxesMode="always" />
                <HeaderFilter visible={true} allowSearch={true} />
                <Column dataField="sFeeName" caption="Fee Name" width={370} />
                <Column dataField="sddsLegalCode" caption="Fee Code" width={120} />
                <Column dataField="sFeeScheduleTypeName" caption="Type" width={120} />
                <Column dataField="iCount" caption="Count" width={120} alignment="left" />
              </DataGrid>
            </div>

            <div style={{ width: "50%" }}>
              <h3 style={{ paddingBottom: "10px" }}>Merge To</h3>

              <div style={{ marginBottom: "20px" }}>
                <label>Select Fee </label>
                <span>*</span>

                <DropDownBox
                  ref={dropDownRef}
                  value={mergeToFee}
                  dataSource={mergeFeeData}
                  valueExpr="gFeeScheduleFeeID"
                  displayExpr="sFeeName"
                  placeholder="Select..."
                  opened={isGridBoxOpened}
                  onOptionChanged={onGridBoxOpened}
                  closeOnOutsideClick={true}
                  contentRender={() => (
                    <DataGrid
                      dataSource={mergeFeeData}
                      keyExpr="gFeeScheduleFeeID"
                      height={600}
                      showBorders={true}
                      hoverStateEnabled={true}
                      selectedRowKeys={mergeToFee ? [mergeToFee] : []}
                      allowColumnResizing={true}
                      columnResizingMode={"nextColumn"}
                      columnAutoWidth={false}
                      onRowClick={(e) => {
                        setMergeToFee(e.data.gFeeScheduleFeeID);
                        setIsGridBoxOpened(false);
                      }}
                    >
                      <Selection mode="single" />
                      <HeaderFilter visible={true} allowSearch={true} />
                      <Column dataField="sFeeName" caption="Fee Name" width={370} />
                      <Column dataField="sddsLegalCode" caption="Fee Code" width={140} />
                      <Column dataField="sFeeScheduleTypeName" caption="Type" width={120} alignment="left" />
                      <Column dataField="iCount" caption="Count" width={120} alignment="left" />
                    </DataGrid>
                  )}
                />
              </div>

              <div style={{ marginTop: "15px" }}>
                <input
                  type="checkbox"
                  checked={deactivateMergeFromFees}
                  onChange={(e) => setDeactivateMergeFromFees(e.target.checked)}
                />
                <label style={{ marginLeft: "8px" }}>Deactivate "Merge From" Fees</label>
              </div>
            </div>
          </div>

          <div className="popup_buttons">
            <button className="btn" onClick={handleCancel}>
              Cancel
            </button>
            <button className="btn primary_btn" onClick={mergeFees}>
              Merge Fees
            </button>
          </div>
        </>
      )}
    </div>
  </>
);
};

export default MergeFees;
